/**
 * Duplicate Signature — Multi-Engine Consensus
 *
 * Shows stocks flagged by multiple engines simultaneously.
 * Higher consensus = higher conviction.
 */
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Snackbar, Alert, Dialog, DialogContent,
  IconButton, TextField, InputAdornment, Skeleton,
} from '@mui/material';
import {
  Refresh, Close, Search, Hub, FiberManualRecord,
  ArrowUpward, ArrowDownward, CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { duplicateSignatureAPI } from '../services/api';
import MarketContextStrip from '../components/MarketContextStrip';

// ── Helpers ──────────────────────────────────────────────────────
const fmtScore = (n) => n != null ? n.toFixed(1) : '—';

const CONSENSUS_CFG = {
  VERY_STRONG: { color: '#10b981', bg: '#ecfdf5', key: 'dupSig.consensus.VERY_STRONG' },
  STRONG:      { color: '#3b82f6', bg: '#eff6ff', key: 'dupSig.consensus.STRONG' },
  MODERATE:    { color: '#f59e0b', bg: '#fffbeb', key: 'dupSig.consensus.MODERATE' },
  WEAK:        { color: '#94a3b8', bg: '#f8fafc', key: 'dupSig.consensus.WEAK' },
  SINGLE:      { color: '#cbd5e1', bg: '#f8fafc', key: 'dupSig.consensus.SINGLE' },
};

const ENGINE_LABELS = {
  detector: { label: 'Detector', color: '#8b5cf6', short: 'DET' },
  flow_leaders: { label: 'Flow Leaders', color: '#3b82f6', short: 'FL' },
  quality_growth: { label: 'Quality Growth', color: '#06b6d4', short: 'QG' },
  super_stock: { label: 'Super Stock', color: '#10b981', short: 'SS' },
  accumulation_breakout: { label: 'Accumulation', color: '#f59e0b', short: 'AB' },
  money_flow: { label: 'Money Flow', color: '#ef4444', short: 'MF' },
  smart_money: { label: 'Smart Money', color: '#6366f1', short: 'SM' },
};

// Layout-accurate skeleton
const PageSkeleton = () => (
  <Box>
    {/* Stat tiles skeleton */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1.2, mb: 2 }}>
      {[1,2,3,4,5,6].map(i => (
        <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #f1f5f9' }}>
          <Skeleton width={60} height={10} sx={{ mb: 0.5 }} />
          <Skeleton width={40} height={24} />
        </Box>
      ))}
    </Box>
    {/* Engine legend skeleton */}
    <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap' }}>
      {[1,2,3,4,5].map(i => <Skeleton key={i} variant="rounded" height={22} sx={{ borderRadius: 1.5, flex: 1 }} />)}
    </Box>
    {/* Search + pills skeleton */}
    <Box sx={{ display: 'flex', gap: 0.6, mb: 1.5, alignItems: 'center' }}>
      <Skeleton variant="rounded" height={32} sx={{ borderRadius: 2, flex: 2 }} />
      {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={24} sx={{ borderRadius: 1.5, flex: 1 }} />)}
    </Box>
    {/* Table skeleton */}
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 1, bgcolor: '#f8fafc' }}>
        {[1, 1.5, 1.5, 1, 1.5, 2.5, 1.5, 1, 2].map((f, i) => <Skeleton key={i} height={14} sx={{ flex: f }} />)}
      </Box>
      {[1,2,3,4,5,6,7,8].map(i => (
        <Box key={i} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.8, borderTop: '1px solid #f1f5f9' }}>
          {[1, 1.5, 1.5, 1, 1.5, 2.5, 1.5, 1, 2].map((f, j) => <Skeleton key={j} height={16} sx={{ flex: f }} />)}
        </Box>
      ))}
    </Box>
  </Box>
);

// Engine dots — shows active engines as colored dots
const EngineDots = ({ engines, t }) => (
  <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
    {Object.entries(engines || {}).map(([eng, active]) => {
      const cfg = ENGINE_LABELS[eng];
      if (!cfg) return null;
      return (
        <Tooltip key={eng} title={`${cfg.label}: ${active ? t('dupSig.engineTip.active') : t('dupSig.engineTip.inactive')}`} arrow>
          <Box sx={{
            width: 18, height: 18, borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: active ? alpha(cfg.color, 0.12) : '#f1f5f9',
            border: `1.5px solid ${active ? cfg.color : '#e2e8f0'}`,
          }}>
            <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: active ? cfg.color : '#cbd5e1' }}>
              {cfg.short[0]}
            </Typography>
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

// Stat tile
const StatTile = ({ label, value, color, sub }) => (
  <Box sx={{
    p: 1.5, borderRadius: 1.5, position: 'relative', overflow: 'hidden',
    background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.03)} 100%)`,
    border: `1.5px solid ${alpha(color, 0.18)}`,
  }}>
    <Box sx={{ position: 'absolute', top: -12, right: -12, width: 48, height: 48, borderRadius: '50%', bgcolor: alpha(color, 0.08) }} />
    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color, fontFamily: 'monospace', lineHeight: 1 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>{sub}</Typography>}
    </Box>
  </Box>
);

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function DuplicateSignature() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('num_engines');
  const [sortAsc, setSortAsc] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [snack, setSnack] = useState(null);
  const [filterConsensus, setFilterConsensus] = useState(null);
  const [filterSector, setFilterSector] = useState(null);

  const results = React.useMemo(() => data?.results || [], [data?.results]);
  const summary = data?.summary || {};

  // Sort & filter
  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(false); }
  };
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return null;
    return sortAsc
      ? <ArrowUpward sx={{ fontSize: 12, ml: 0.3, verticalAlign: 'middle' }} />
      : <ArrowDownward sx={{ fontSize: 12, ml: 0.3, verticalAlign: 'middle' }} />;
  };

  // Sector display helper — sector is {vi, en} or legacy string
  const sectorName = (sec) => {
    if (!sec) return '—';
    if (typeof sec === 'object') return sec[lang] || sec.en || '—';
    return sec;
  };
  const sectorKey = (sec) => {
    if (!sec) return null;
    if (typeof sec === 'object') return sec.en || sec.vi || null;
    return sec;
  };

  // Unique sectors
  const sectors = React.useMemo(() => {
    const map = new Map();
    results.forEach(r => {
      const key = sectorKey(r.sector);
      if (key && !map.has(key)) map.set(key, r.sector);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [results]);

  const filtered = results
    .filter(r => !search || r.ticker?.toLowerCase().includes(search.toLowerCase()))
    .filter(r => !filterConsensus || r.consensus_label === filterConsensus)
    .filter(r => !filterSector || sectorKey(r.sector) === filterSector)
    .sort((a, b) => {
      const va = a[sortCol] ?? 0, vb = b[sortCol] ?? 0;
      return sortAsc ? va - vb : vb - va;
    });

  // Scan
  const runScan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await duplicateSignatureAPI.scan({ min_engines: 2 });
      setData(resp.data);
      setSnack({ severity: 'success', msg: t('dupSig.snack.success', { count: resp.data.total }) });
    } catch (e) {
      setError(e.message);
      setSnack({ severity: 'error', msg: t('dupSig.snack.error') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Auto-load
  React.useEffect(() => { runScan(); }, [runScan]);

  const consDist = summary.consensus_distribution || {};

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Hub sx={{ fontSize: 28, color: '#6366f1' }} />
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#1e293b', lineHeight: 1.1 }}>
              {t('dupSig.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
              {t('dupSig.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" size="small" startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
          onClick={runScan} disabled={loading}
          sx={{ fontSize: '0.72rem', textTransform: 'none', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
          {loading ? t('dupSig.scanning') : t('dupSig.scanNew')}
        </Button>
      </Box>

      {/* Market Context Strip */}
      <MarketContextStrip standalone />

      {/* Summary stats */}
      {data && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1.2, mb: 2 }}>
          <StatTile label={t('dupSig.stats.total')} value={summary.total || 0} color="#1e293b" />
          <StatTile label={t('dupSig.stats.veryStrong')} value={consDist.VERY_STRONG || 0} color="#10b981" />
          <StatTile label={t('dupSig.stats.strong')} value={consDist.STRONG || 0} color="#3b82f6" />
          <StatTile label={t('dupSig.stats.moderate')} value={consDist.MODERATE || 0} color="#f59e0b" />
          <StatTile label={t('dupSig.stats.weak')} value={consDist.WEAK || 0} color="#94a3b8" />
          {summary.avg_engines != null && (
            <StatTile label={t('dupSig.stats.avgEngines')} value={summary.avg_engines} color="#6366f1" sub={`max: ${summary.max_engines || 0}`} />
          )}
        </Box>
      )}

      {/* Engine overlap legend */}
      {data && summary.engine_overlap_counts && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('dupSig.engineLegend')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
          {Object.entries(summary.engine_overlap_counts || {}).filter(([,c]) => c > 0).map(([eng, count]) => {
            const cfg = ENGINE_LABELS[eng];
            if (!cfg) return null;
            return (
              <Box key={eng} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, borderRadius: 2, bgcolor: '#fff', border: `1px solid ${alpha(cfg.color, 0.2)}` }}>
                <FiberManualRecord sx={{ fontSize: 7, color: cfg.color }} />
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569' }}>{cfg.label}</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: cfg.color, fontFamily: 'monospace' }}>{count}</Typography>
              </Box>
            );
          })}
          </Box>
        </Box>
      )}

      {/* Search + consensus filters */}
      {data && results.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
            <TextField size="small" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('dupSig.search')} variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
                sx: { fontSize: '0.78rem', borderRadius: 2, height: 32, '& fieldset': { borderColor: '#e2e8f0' } },
              }}
              sx={{ width: 180 }}
            />
            {/* Consensus filter pills */}
            {Object.entries(consDist).filter(([,c]) => c > 0).map(([key, count]) => {
              const cfg = CONSENSUS_CFG[key] || CONSENSUS_CFG.WEAK;
              const active = filterConsensus === key;
              return (
                <Box key={key} onClick={() => setFilterConsensus(active ? null : key)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.7, py: 0.3, borderRadius: 1.5, cursor: 'pointer',
                    bgcolor: active ? alpha(cfg.color, 0.18) : cfg.bg,
                    border: `1.5px solid ${alpha(cfg.color, active ? 0.5 : 0.15)}`,
                    transition: 'all 0.15s' }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: cfg.color }}>{t(cfg.key)}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: cfg.color, fontFamily: 'monospace' }}>{count}</Typography>
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>{filtered.length}</Box> / {results.length}
          </Typography>
        </Box>
      )}

      {/* Sector pills — own row for visual clarity */}
      {data && sectors.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
          {sectors.map(([key, secObj]) => (
            <Chip key={key} label={sectorName(secObj)} size="small" onClick={() => setFilterSector(filterSector === key ? null : key)}
              sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20, cursor: 'pointer',
                bgcolor: filterSector === key ? alpha('#6366f1', 0.15) : '#f1f5f9',
                color: filterSector === key ? '#4f46e5' : '#64748b',
                border: `1px solid ${filterSector === key ? '#6366f1' : '#e2e8f0'}`,
                transition: 'all 0.15s' }} />
          ))}
        </Box>
      )}

      {/* Loading / Error / Empty */}
      {loading && !data && <PageSkeleton />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {data && results.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
          <Hub sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography sx={{ fontWeight: 600 }}>{t('dupSig.empty.title')}</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>{t('dupSig.empty.subtitle')}</Typography>
        </Box>
      )}

      {/* Table */}
      {filtered.length > 0 && (
        <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc' } }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', width: 36 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('dupSig.table.ticker')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('dupSig.table.price')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('num_engines')}>
                  {t('dupSig.table.engines')} <SortIcon col="num_engines" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('dupSig.table.consensus')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('dupSig.table.activeEngines')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('best_score')}>
                  {t('dupSig.table.bestScore')} <SortIcon col="best_score" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('avg_score')}>
                  {t('dupSig.table.avg')} <SortIcon col="avg_score" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('dupSig.table.sector')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r, i) => {
                const cons = CONSENSUS_CFG[r.consensus_label] || CONSENSUS_CFG.WEAK;
                return (
                  <TableRow key={r.ticker} hover onClick={() => setDetailRow(r)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#6366f1', 0.03) } }}>
                    <TableCell sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                    <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{r.ticker}</Typography></TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace' }}>{r.close?.toLocaleString() || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', bgcolor: alpha(cons.color, 0.1), color: cons.color, fontSize: '0.82rem', fontWeight: 900 }}>
                        {r.num_engines}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: cons.color }}>{t(cons.key)}</Typography>
                    </TableCell>
                    <TableCell><EngineDots engines={r.engines} t={t} /></TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: r.best_score >= 70 ? '#10b981' : '#64748b' }}>
                      {fmtScore(r.best_score)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace', color: '#64748b' }}>
                      {fmtScore(r.avg_score)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: '#64748b' }}>{sectorName(r.sector)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Detail Dialog ──────────────────────────────────────── */}
      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', maxWidth: 420 } }}>
        {detailRow && (() => {
          const cons = CONSENSUS_CFG[detailRow.consensus_label] || CONSENSUS_CFG.WEAK;
          return (
            <>
              <Box sx={{ px: 2, pt: 1.5, pb: 1.2, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#1e293b' }}>{detailRow.ticker}</Typography>
                    <Chip label={t(cons.key)} size="small" sx={{ fontWeight: 800, fontSize: '0.62rem', height: 20, bgcolor: cons.bg, color: cons.color, border: `1px solid ${alpha(cons.color, 0.3)}` }} />
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', bgcolor: alpha(cons.color, 0.1), color: cons.color, fontSize: '0.72rem', fontWeight: 900 }}>
                      {detailRow.num_engines}
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setDetailRow(null)} sx={{ color: '#94a3b8' }}><Close /></IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{t('dupSig.detail.bestScore')}</Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, fontFamily: 'monospace', color: cons.color, lineHeight: 1 }}>
                      {detailRow.best_score.toFixed(1)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.5rem', color: '#cbd5e1' }}>│</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{t('dupSig.detail.avg')}</Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: 'monospace', color: '#475569', lineHeight: 1 }}>
                      {detailRow.avg_score.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <DialogContent sx={{ p: 0 }}>
                {/* Engine breakdown */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', mb: 1, textTransform: 'uppercase' }}>
                    {t('dupSig.detail.engineBreakdown')}
                  </Typography>
                  {Object.entries(detailRow.engines || {}).map(([eng, active]) => {
                    const cfg = ENGINE_LABELS[eng];
                    if (!cfg) return null;
                    const score = detailRow.engine_scores?.[eng];
                    const rank = detailRow.engine_ranks?.[eng];
                    return (
                      <Box key={eng} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f8fafc' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FiberManualRecord sx={{ fontSize: 8, color: active ? cfg.color : '#e2e8f0' }} />
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: active ? 700 : 400, color: active ? '#1e293b' : '#cbd5e1' }}>
                            {cfg.label}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {active && score != null && (
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: score >= 70 ? '#10b981' : score >= 50 ? '#3b82f6' : '#f59e0b' }}>
                              {score.toFixed(1)}
                            </Typography>
                          )}
                          {active && rank != null && (
                            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>#{rank}</Typography>
                          )}
                          {active ? (
                            <CheckCircle sx={{ fontSize: 14, color: cfg.color }} />
                          ) : (
                            <Close sx={{ fontSize: 14, color: '#e2e8f0' }} />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {detailRow.sector && sectorName(detailRow.sector) !== '—' && (
                  <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                      {t('dupSig.detail.sector')} <strong>{sectorName(detailRow.sector)}</strong>
                    </Typography>
                  </Box>
                )}
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack && <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ fontSize: '0.78rem' }}>{snack.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}
